import React from 'react';
import { connect } from 'react-redux';
import { IonButton, IonIcon, IonInput, IonItem } from '@ionic/react';
import { add, chevronForwardOutline, giftOutline } from 'ionicons/icons';
import moment from 'moment';
import Layout from '../../components/layout';
import Loading from '../../components/spinner';
import Modal from '../../components/modal';
import SuccessModal from '../../components/successModal';
import { NormalText, SmallText, Spacer, StrongText, Subtitle, Title } from '../../components/common';
import { withTranslation } from '../../lib/translate';
import { forwardTo, getGiftCardNumber, getMaskedGiftCardNumber } from '../../lib/utils';
import api from '../../lib/api';
import { getConfig } from '../../appConfig.js';
import Basket from '../../lib/basket';
import { getGiftCards, loading, showToast } from '../../store/actions';
import './index.css';

class GiftCards extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			claimModalOpen: false,
			voucherId: '',
			securityCode: '',
			claimedModalOpen: false,
		};
	}

	componentDidMount() {
		if (this.props.auth.loggedIn) {
			this.props.dispatch(getGiftCards());
		}
	}

	setClaimModalOpen = (value) => {
		if (value) {
			this.setState({ voucherId: '', securityCode: '' });
		}
		this.setState({ claimModalOpen: value });
	};

	claimGiftCard = async () => {
		const { __, dispatch } = this.props;
		const { voucherId, securityCode } = this.state;
		dispatch(loading(true));
		try {
			const result = await api.redeemGiftVoucher({ ...(voucherId && { voucher_id: voucherId }), ...(securityCode && { code: securityCode }) });
			if (result.error) {
				dispatch(showToast(__(result.error.message || 'An error occurred. Please try again'), 'warning'));
				return;
			}
			this.setClaimModalOpen(false);
			dispatch(getGiftCards());
			this.setState({ claimedModalOpen: true });
		} catch (err) {
			dispatch(showToast(__(err.response?.data?.error?.message || 'An error occurred. Please try again'), 'warning'));
		} finally {
			dispatch(loading(false));
		}
	};

	render() {
		const { __, giftCards } = this.props;
		const { claimModalOpen, claimedModalOpen, voucherId, securityCode } = this.state;
		const { removeVoucherNumberFromGiftCardClaim, removeRedemptionCodeFromGiftCardClaim, showFullGiftCardNumber } = getConfig().flags;
		const today = moment().startOf('day');
		const cards = giftCards.filter(el => !el.removed).map(card => {
			const expiryDate = moment(card.expiry_date, 'DD/MM/YYYY').startOf('day');
			return {
				card,
				expiryDate,
				isExpired: expiryDate.isBefore(today),
				expiresToday: expiryDate.isSame(today),
				isUsed: !(card.value_remaining > 0),
			};
		});
		const activeCards = cards.filter(el => !el.isExpired && !el.isUsed);
		const expiredCards = cards.filter(el => el.isExpired && !el.isUsed);
		const completedCards = cards.filter(el => el.isUsed);
		const totalBalance = activeCards.reduce((sum, el) => sum + el.card.value_remaining, 0);
		const sections = [
			{ title: 'Active', items: activeCards },
			{ title: 'Expired', items: expiredCards },
			{ title: 'Completed', items: completedCards },
		];
		
		return (
			<Loading transparent>
				<Layout showHamburger headerWithTitle title={__('My gift cards')} color="transparent">
					<div className="absolute-content scrollable-y flex-row-wrapper box-with-shadow">
						<div>
							<Title className="web-only page-title-centered ">{__('My gift cards')}</Title>
							{cards.length > 0 ? (
								<>
									<div className="box-content flex-wrapper flex-justify-between flex-align-center">
										<StrongText>{__('Total balance')}</StrongText>
										<StrongText >{Basket.formatPrice(totalBalance / 100, true)}</StrongText>
									</div>
									{sections.filter(section => section.items.length > 0).map(section => (
										<div key={section.title}>
											<Spacer size={2} />
											<StrongText className="block text-to-update">{__(section.title)}</StrongText>
											<Spacer size={2} />
											{section.items.map(({ card, expiryDate, isExpired, expiresToday, isUsed }, giftCardIndex) => {
												const daysLeft = expiryDate.diff(today, 'days');
												const lastSpent = (card.history || []).filter(el => el.type === 'value_spent').sort((a, b) => moment(b.timestamp) - moment(a.timestamp))[0];
												// A card with no expiry (gift_cards.expiry_months 0) has no date to show.
												let statusText = expiryDate.isValid() ? `${__('Expires')} ${expiryDate.format('LLLL')}` : '';
												let isWarning = false;
												if (isUsed) {
													statusText = lastSpent ? `${__('Fully used')} ${moment(lastSpent.timestamp).format('LLLL')}` : __('Fully used');
												} else if (isExpired) {
													statusText = `${__('Expired')} ${expiryDate.format('LLLL')}`;
												} else if (expiresToday) {
													statusText = __('Expires today');
													isWarning = true;
												} else if (daysLeft <= 30) {
													statusText = `${__('Expires in')} ${daysLeft} ${__(daysLeft === 1 ? 'day' : 'days')}`;
													isWarning = true;
												}
												return (
													<div
														key={`${section.title}_${giftCardIndex}`}
														className={`box-content flex-wrapper flex-align-center pointer gift-cards-item${isExpired || isUsed ? ' gift-cards-item-inactive' : ''}`}
														onClick={() => forwardTo('/gift-card', { card, isExpired, expiresToday })}
													>
														<div className="gift-cards-icon flex-wrapper flex-align-center flex-justify-center">
															<IonIcon icon={giftOutline} />
														</div>
														<div className="gift-cards-item-details">
															<StrongText className="block text-to-update">{showFullGiftCardNumber ? __('Gift card') : `${__('Gift card')} ${getMaskedGiftCardNumber(card)}`}</StrongText>
															{showFullGiftCardNumber && <SmallText className="block">{getGiftCardNumber(card)}</SmallText>}
															<SmallText className={`block${isWarning ? ' danger-color bold' : ''}`}>{statusText}</SmallText>
														</div>
														<StrongText>{Basket.formatPrice(card.value_remaining / 100, true)}</StrongText>
														<IonIcon icon={chevronForwardOutline} />
													</div>
												);
											})}
										</div>
									))}
								</>
							) : (
								<div className="no-data-history centered">
									<IonIcon className="gift-cards-empty-icon" color="primary" icon={giftOutline} />
									<Spacer size={1} />
									<Title>{__('No gift cards yet')}</Title>
									<Spacer size={1} />
									<NormalText>{__('Your saved gift cards will appear here when added to your account')}</NormalText>
								</div>
							)}
						</div>
						<div className="flex-min">
							<Spacer size={5} />
							<IonButton className="button-uppercase" color="primary" expand="block" onClick={() => this.setClaimModalOpen(true)}>
								<IonIcon slot="start" icon={add} />
								{__('Claim gift card')}
							</IonButton>
						</div>
					</div>
					<Modal onDidDismiss={() => this.setClaimModalOpen(false)} isOpen={claimModalOpen} className="modal-type-alert">
						<StrongText className="block text-to-update">{__('Redeem gift voucher')}</StrongText>
						<Spacer size={5} />
						{!removeVoucherNumberFromGiftCardClaim && (
							<div className="input-field-container">
								<StrongText>{__('Voucher number')}</StrongText>
								<IonItem className="input-field-wrapper" lines="none">
									<IonInput
										placeholder={__('e.g. 40021880')}
										type="text"
										inputmode="numeric"
										pattern="[0-9]*"
										onIonInput={(e) => {
											const voucherId = (e.target.value || '').replace(/[^0-9]/g, '');
											e.target.value = voucherId;
											this.setState({ voucherId });
										}}
										value={voucherId}
									></IonInput>
								</IonItem>
							</div>
						)}
						{!removeRedemptionCodeFromGiftCardClaim && (
							<div className="input-field-container">
								<StrongText>{__('Redemption code')}</StrongText>
								<IonItem className="input-field-wrapper" lines="none">
									<IonInput
										placeholder={__('e.g. FRYUP')}
										type="text"
										inputmode="text"
										autocapitalize="characters"
										onIonInput={(e) => {
											const securityCode = (e.target.value || '').toUpperCase();
											e.target.value = securityCode;
											this.setState({ securityCode });
										}}
										value={securityCode}
									></IonInput>
								</IonItem>
							</div>
						)}
						<div className="alert-button-group">
							<IonButton color="secondary" expand="block" onClick={() => this.setClaimModalOpen(false)} className="submit-button button-uppercase">
								{__('Cancel')}
							</IonButton>
							<IonButton disabled={(!removeVoucherNumberFromGiftCardClaim && !voucherId) || (!removeRedemptionCodeFromGiftCardClaim && !securityCode)} color="primary" expand="block" onClick={this.claimGiftCard} className="submit-button button-uppercase">
								{__('Claim')}
							</IonButton>
						</div>
					</Modal>
					<SuccessModal
						isOpen={claimedModalOpen}
						onDismiss={() => this.setState({ claimedModalOpen: false })}
						title={__('Gift card claimed')}
						message={__('The gift card has been added to your account and is ready to be used.')}
					/>
				</Layout>
			</Loading>
		);
	}
}

const stateToProps = (state) => {
	return {
		giftCards: state.profile.giftCards || [],
		auth: state.profile.auth,
	};
};

export default connect(stateToProps)(withTranslation(GiftCards));
